import {Checkbox, FlexRow, IconTooltip, Modal} from '@forecast-it/design-system';
import {PDFViewer, usePDF} from '@react-pdf/renderer';
import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {InvoicePDFDownloadModal_viewer} from './__generated__/InvoicePDFDownloadModal_viewer.graphql';
import InvoicePDF from './InvoicePDF';
import {OptionalInvoiceExportField} from './InvoicePDFTypes';

const SHOW_INLINE_PDF_VIEWER = false;

const OptionalFieldsTranslationKeyMap = new Map<
	OptionalInvoiceExportField,
	{
		checkboxLabel: string;
		tooltipTitle?: string;
		tooltipDetails?: string;
	}
>([
	[
		OptionalInvoiceExportField.CLIENT_ADDRESS,
		{
			checkboxLabel: 'invoice.pdf_optional_info.client_address',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_client_address.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_client_address.details',
		},
	],
	[
		OptionalInvoiceExportField.COMPANY_ADDRESS,
		{
			checkboxLabel: 'invoice.pdf_optional_info.company_address',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_company_address.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_company_address.details',
		},
	],
	[
		OptionalInvoiceExportField.COMPANY_VAT,
		{
			checkboxLabel: 'invoice.pdf_optional_info.company_vat',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_company_vat.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_company_vat.details',
		},
	],
	[
		OptionalInvoiceExportField.COMPANY_LOGO,
		{
			checkboxLabel: 'invoice.pdf_optional_info.company_logo',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_company_logo.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_company_logo.details',
		},
	],
	[
		OptionalInvoiceExportField.INVOICE_ID,
		{
			checkboxLabel: 'invoice.pdf_optional_info.invoice_id',
		},
	],
	[
		OptionalInvoiceExportField.INVOICE_REFERENCE,
		{
			checkboxLabel: 'invoice.pdf_optional_info.invoice_reference',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_invoice_reference.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_invoice_reference.details',
		},
	],
	[
		OptionalInvoiceExportField.INVOICE_NOTES,
		{
			checkboxLabel: 'invoice.pdf_optional_info.invoice_notes',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_invoice_notes.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_invoice_notes.details',
		},
	],
	[
		OptionalInvoiceExportField.INVOICE_NAME,
		{
			checkboxLabel: 'invoice.pdf_optional_info.invoice_name',
			tooltipTitle: 'invoice.pdf_optional_info.tooltips.no_invoice_name.title',
			tooltipDetails: 'invoice.pdf_optional_info.tooltips.no_invoice_name.details',
		},
	],
]);

interface InvoicePDFDownloadModalProps {
	viewer: InvoicePDFDownloadModal_viewer;
	closeModal: () => void;
}

const InvoicePDFDownloadModal: React.FC<InvoicePDFDownloadModalProps> = ({viewer, closeModal}) => {
	const intl = useIntl();
	const {invoice, company} = viewer;
	const invoiceFileName = `invoice_${invoice?.companyInvoiceId?.toString()}_export`;

	const [clientAddressChecked, setClientAddressChecked] = useState(true);
	const [companyAddressChecked, setCompanyAddressChecked] = useState(true);
	const [companyVatChecked, setCompanyVatChecked] = useState(true);
	const [companyLogoChecked, setCompanyLogoChecked] = useState(true);
	const [invoiceIdChecked, setInvoiceIdChecked] = useState(true);
	const [invoiceReferenceChecked, setInvoiceReferenceChecked] = useState(false);
	const [invoiceNotesChecked, setInvoiceNotesChecked] = useState(false);
	const [invoiceNameChecked, setInvoiceNameChecked] = useState(false);
	const [timeRegistrationsChecked, setTimeRegistrationsChecked] = useState(false);

	const OptionalFieldsSetterMap = new Map([
		[OptionalInvoiceExportField.CLIENT_ADDRESS, setClientAddressChecked],
		[OptionalInvoiceExportField.COMPANY_ADDRESS, setCompanyAddressChecked],
		[OptionalInvoiceExportField.COMPANY_VAT, setCompanyVatChecked],
		[OptionalInvoiceExportField.COMPANY_LOGO, setCompanyLogoChecked],
		[OptionalInvoiceExportField.INVOICE_ID, setInvoiceIdChecked],
		[OptionalInvoiceExportField.INVOICE_REFERENCE, setInvoiceReferenceChecked],
		[OptionalInvoiceExportField.INVOICE_NOTES, setInvoiceNotesChecked],
		[OptionalInvoiceExportField.INVOICE_NAME, setInvoiceNameChecked],
		[OptionalInvoiceExportField.TIME_REGISTRATIONS, setTimeRegistrationsChecked],
	]);

	const pdf = useMemo(() => {
		const optionalFields = {
			clientAddressChecked,
			companyAddressChecked,
			companyVatChecked,
			companyLogoChecked,
			invoiceIdChecked,
			invoiceReferenceChecked,
			invoiceNotesChecked,
			invoiceNameChecked,
			timeRegistrationsChecked,
		};
		return {
			fileName: invoiceFileName,
			document: <InvoicePDF invoiceData={invoice} companyData={company} intl={intl} optionalFields={optionalFields} />,
		};
	}, [
		clientAddressChecked,
		companyAddressChecked,
		companyVatChecked,
		companyLogoChecked,
		invoiceIdChecked,
		invoiceReferenceChecked,
		invoiceNotesChecked,
		invoiceNameChecked,
		timeRegistrationsChecked,
		invoice,
		company,
	]);

	const [pdfInstance, setPDFDocument] = usePDF({});

	const onExportPdf = () => {
		if (pdfInstance.url) {
			trackEvent('Invoice PDF File', 'Exported');
			const downloadLink = document.createElement('a');
			downloadLink.href = pdfInstance.url;
			downloadLink.download = invoiceFileName;

			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
		}
		closeModal();
	};

	useEffect(() => {
		setPDFDocument(pdf.document);
	}, [pdf.document]);

	if (pdfInstance.loading) {
		console.log('PDF is loading');
	} else {
		console.log('PDF is ready');
	}

	if (pdfInstance.error) {
		console.log('PDF error', pdfInstance.error);
	}

	const onToggle = (field: OptionalInvoiceExportField) => (checked: boolean) =>
		(OptionalFieldsSetterMap.get(field) || (() => null))(checked);

	const OptionalFieldCheckbox = ({
		field,
		checked,
		enabled,
	}: {
		field: OptionalInvoiceExportField;
		checked: boolean;
		enabled: boolean;
	}) => {
		const translationKeyData = OptionalFieldsTranslationKeyMap.get(field);
		const tooltipData = translationKeyData?.tooltipTitle &&
			translationKeyData?.tooltipDetails && {
				title: intl.formatMessage({id: translationKeyData?.tooltipTitle}),
				details: intl.formatMessage({id: translationKeyData?.tooltipDetails}),
			};

		return (
			<FlexRow gap={'s'}>
				<Checkbox checked={checked && enabled} onChange={onToggle(field)} disabled={!enabled}></Checkbox>
				<FormattedMessage id={translationKeyData?.checkboxLabel} />
				{!enabled && translationKeyData?.tooltipTitle && (
					<IconTooltip>
						<IconTooltip.Item content={tooltipData} />
					</IconTooltip>
				)}
			</FlexRow>
		);
	};
	const clientAddressAdded =
		!!invoice?.client?.street || !!invoice?.client?.zip || !!invoice?.client?.city || !!invoice?.client?.country;
	const companyAddressAdded = !!company?.street || !!company?.zip || !!company?.city || !!company?.country;

	const companyVatAdded = !!company?.vatId;

	const companyLogoAdded = !!company?.logoId;
	const invoiceReferenceAdded = !!invoice?.invoiceReference;
	const invoiceNotesAdded = !!invoice?.notes;
	const invoiceNameAdded = !!invoice?.name;

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id={'invoice.download_pdf_options'} />
			</Modal.Title>
			<Modal.Content>
				<FormattedMessage id={'invoice.select_pdf_optional_info'} />
				<OptionalFieldCheckbox
					checked={clientAddressChecked}
					field={OptionalInvoiceExportField.CLIENT_ADDRESS}
					enabled={clientAddressAdded}
				/>
				<OptionalFieldCheckbox
					checked={companyAddressChecked}
					field={OptionalInvoiceExportField.COMPANY_ADDRESS}
					enabled={companyAddressAdded}
				/>
				<OptionalFieldCheckbox
					checked={companyVatChecked}
					field={OptionalInvoiceExportField.COMPANY_VAT}
					enabled={companyVatAdded}
				/>
				<OptionalFieldCheckbox
					checked={companyLogoChecked}
					field={OptionalInvoiceExportField.COMPANY_LOGO}
					enabled={companyLogoAdded}
				/>
				<OptionalFieldCheckbox
					checked={invoiceIdChecked}
					field={OptionalInvoiceExportField.INVOICE_ID}
					enabled={true}
				/>
				<OptionalFieldCheckbox
					checked={invoiceReferenceChecked}
					field={OptionalInvoiceExportField.INVOICE_REFERENCE}
					enabled={invoiceReferenceAdded}
				/>
				<OptionalFieldCheckbox
					checked={invoiceNameChecked}
					field={OptionalInvoiceExportField.INVOICE_NAME}
					enabled={invoiceNameAdded}
				/>
				<OptionalFieldCheckbox
					checked={invoiceNotesChecked}
					field={OptionalInvoiceExportField.INVOICE_NOTES}
					enabled={invoiceNotesAdded}
				/>
				{SHOW_INLINE_PDF_VIEWER && (
					<div style={{height: '600px', display: 'flex'}}>
						<PDFViewer width={'100%'}>{pdf.document}</PDFViewer>
					</div>
				)}
				{/*
				Commented out for now, as not implemented yet
				<OptionalFieldCheckbox
					checked={timeRegistrationsChecked}
					field={OptionalInvoiceExportField.TIME_REGISTRATIONS}
				/>*/}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onExportPdf}>
					<FormattedMessage id={'invoice.download_pdf'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

const InvoicePDFDownloadModalQuery = graphql`
	query InvoicePDFDownloadModalQuery($invoiceId: ID) {
		viewer {
			actualPersonId
			component(name: "invoice_pdf_download_modal")
			...InvoicePDFDownloadModal_viewer @arguments(invoiceId: $invoiceId)
		}
	}
`;

export {InvoicePDFDownloadModalQuery};

export default createFragmentContainer(InvoicePDFDownloadModal, {
	viewer: graphql`
		fragment InvoicePDFDownloadModal_viewer on Viewer @argumentDefinitions(invoiceId: {type: "ID"}) {
			company {
				logoId
				name
				street
				zip
				city
				country
				vatId
			}
			invoice(id: $invoiceId) {
				id
				name
				companyInvoiceId
				currency
				notes
				invoiceReference
				dueYear
				dueMonth
				dueDay
				createdYear
				createdMonth
				createdDay
				client {
					name
					street
					zip
					city
					country
				}
				entries(first: 1000) {
					edges {
						node {
							id
							description
							quantity
							unitPrice
							discount
							tax
							project {
								id
								name
							}
						}
					}
				}
			}
		}
	`,
});
